import LiveApiClient from "../api/LiveApiClient";
import { DB } from "./Cache.util";
import { CanceledError } from "axios";

export class ProductUtil {
	static #PreloadedData;

	/**
	 * @param {Item} options.product
	 * @param options.profile_token
	 * @param options.organization_id
	 * @returns {URL|undefined}
	 */
	static Make_Pdf_Qr_Code_Url(options) {
		if (!options.product?.pdf) {
			return;
		}

		const qr_url = new URL("/qr", "https://bosslogics.live"); // TODO ... env variable for prod live site
		options.product?.id && qr_url.searchParams.set("product", options.product.id);

		const url = this.Make_Pdf_Url(options);
		qr_url.searchParams.set("url", url.toString());

		return qr_url;
	}

	/**
	 * @param {Item} options.product
	 * @param options.profile_token
	 * @param options.organization_id
	 * @returns {URL|undefined}
	 */
	static Make_Pdf_Url(options) {
		if (!options.product?.pdf) {
			return;
		}

		const url = new URL(options.product.pdf);
		options.profile_token && url.searchParams.set("profile", options.profile_token);
		options.organization_id && url.searchParams.set("org", options.organization_id.toString());

		return url;
	}

	static GetDifferences(a, b) {
		const variableAttributes = this.CurrentAttributes?.filter(attribute => attribute.values?.length > 1);
		if (!variableAttributes) {
			return [];
		}
		return variableAttributes.filter(({ reference }) => a?.[reference] !== b?.[reference]);
	};
	static GetPreloadedProduct(id) {
		return this.#PreloadedData?.items?.[id];
	};

	static async PreloadProductGroup({ catalog, groupField, groupCode, attributes, loadOptions } = {}) {
		this.CurrentAttributes = attributes;

		this.#PreloadedData ??= {};
		this.#PreloadedData.items ??= {};
		this.#PreloadedData.item_group_pages ??= {};
		this.#PreloadedData.item_group ??= {};
		this.#PreloadedData.item_group_pages[groupCode] ??= {};

		loadOptions.signal = loadOptions.abortController.signal;
		const dbName = DB.Make_Name(catalog.id);

		const limit = loadOptions.limit || 100;
		let offset = Object.values(this.#PreloadedData.item_group_pages[groupCode]).length * limit;
		const filter = { '{status}': 1, '{active}': 1 };
		filter[`{${groupField}}`] = encodeURIComponent(groupCode);

		// TODO: Use x-total-count header to determine if we need to load more pages
		loadOptions.params = { filter: JSON.stringify(filter), limit, offset };
		let itemsChunk = null;

		do {
			let cacheKey = ["PreloadProductGroup", "Group", groupCode, "Offset", offset];
			itemsChunk = this.#PreloadedData.item_group_pages[groupCode][offset]
				|| await DB.Get(dbName, cacheKey);
			if (!itemsChunk) {
				try {
					const params = { ...loadOptions.params, offset };
					itemsChunk = await LiveApiClient.get(`/Item/`, { ...loadOptions, params }).then(response => response.data);
				} catch (error) {
					if (!(error instanceof CanceledError)) {
						console.error(error);
					}
					return;
				}

				if (dbName) {
					DB.Set(dbName, cacheKey, itemsChunk);
				}
			}
			this.#PreloadedData.item_group_pages[groupCode][offset] = itemsChunk;
			offset += limit;
		} while (itemsChunk.length);

		let items = [];
		Object.values(this.#PreloadedData.item_group_pages[groupCode]).forEach(pagedItems => items = items.concat(pagedItems));
		items.forEach(item => this.#PreloadedData.items[item.id] = item);
		this.#PreloadedData.item_group[groupCode] = items;

		const variableAttributes = attributes.filter(attribute => attribute.values?.length > 1);

		const getDistance = (a, b) =>
			variableAttributes
				.reduce((acc, { reference }) => acc + (a[reference] !== b[reference] ? 1 : 0), 0);

		// Optimized for speed: performs 30% faster than using sort() inside loop
		const items_by_attribute_value = {};
		variableAttributes.forEach(({ reference, values }) => {
			items_by_attribute_value[reference] = { '': [] };
			values.forEach(value => items_by_attribute_value[reference][value?.text] = [])
		});
		items.forEach(item => variableAttributes.forEach(({ reference }) => {
			// Every attribute variation must have an array
			items_by_attribute_value[reference][item[reference] || ''] ??= [];
			items_by_attribute_value[reference][item[reference] || ''].push(item);
		}));

		const findLowestDistance = (reference, value, distances) => {
			let lowest = false;
			let lowestDistance = false;
			const items = items_by_attribute_value[reference][value.text || ''];
			const len = items.length;

			// Use for loop for better performance
			for (let i = 0;i < len;i++) {
				if (items[i][reference] === value.text) {
					const currentDistance = distances[items[i].id];
					if (!lowest || currentDistance < lowestDistance) {
						lowest = items[i];
						lowestDistance = currentDistance;
						if (lowestDistance < 1) {
							break; // Found a match
						}
					}
				}
			}
			return lowest;
		}

		// Calculate variations
		items.forEach(item => {
			const distances = {};
			item.variations = {};
			items.forEach(otherItem => distances[otherItem.id] = getDistance(item, otherItem));

			variableAttributes.forEach(({ reference, values }) => {
				item.variations[reference] = [];

				values.forEach(value => {
					const variation = findLowestDistance(reference, value, distances);
					if (variation) {
						const record = [
							{ ...value, image: variation.image_0 },
							variation.id,
							distances[variation.id],
							variation,
						];
						item.variations[reference].push(record);
					}
				});
			});
		});
	}
}